import { defineStore } from 'pinia';
import { pcCardAssetList } from '@/api/wallet';
import { useProfileStore } from '@/stores/profile';

function defaultState() {
  return {
    assetList: [],
    currentAsset: null
  };
}

function normalizeAsset(item) {
  const balance = Number(item?.balanceNormal ?? item?.balance ?? 0);
  const balanceTo = Number(item?.balanceTo ?? item?.totalTo ?? 0);
  const usdtPrice = balance > 0 ? balanceTo / balance : Number(item?.usdtPrice ?? item?.price ?? 0);

  return {
    ...item,
    coin: item?.symbol || item?.coin || item?.name || '',
    symbol: item?.symbol || item?.coin || '',
    balance: String(item?.balanceNormal ?? item?.balance ?? '0'),
    balanceTo,
    usdtPrice,
    img: item?.logo || item?.img || '',
    contractAddress: item?.contractAddress || '',
    symbolType: item?.symbolType || '',
    decimalNum: Number(item?.decimalNum ?? item?.decimals ?? 18),
    coinType: Number(item?.coinType ?? (item?.contractAddress ? 2 : 1)),
    rpcUrl: item?.rpcUrl || ''
  };
}

function mergeLocalTokens(list = [], localTokens = []) {
  const merged = Array.isArray(list) ? list.map(normalizeAsset) : [];
  const contractSet = new Set(
    merged
      .map((item) => String(item?.contractAddress || '').trim().toLowerCase())
      .filter(Boolean)
  );

  (Array.isArray(localTokens) ? localTokens : []).forEach((item) => {
    const normalized = normalizeAsset(item);
    const key = String(normalized?.contractAddress || '').trim().toLowerCase();
    if (key && !contractSet.has(key)) {
      merged.push({
        ...normalized,
        balance: String(normalized?.balance || '0'),
        balanceTo: Number(normalized?.balanceTo || 0),
        usdtPrice: Number(normalized?.usdtPrice || 0)
      });
      contractSet.add(key);
    }
  });

  return merged;
}

function isSameAsset(asset, payload = {}) {
  const contractAddress = String(payload.contractAddress || '').trim().toLowerCase();
  const symbol = String(payload.symbol || payload.coin || '').trim().toUpperCase();
  const assetContractAddress = String(asset?.contractAddress || '').trim().toLowerCase();
  const assetSymbol = String(asset?.symbol || asset?.coin || '').trim().toUpperCase();

  if (contractAddress && assetContractAddress) {
    return contractAddress === assetContractAddress;
  }

  return !!symbol && symbol === assetSymbol;
}

export const useWalletTokenStore = defineStore('wallet-token', {
  state: () => defaultState(),
  getters: {
    findAssetByKey: (state) => (payload = {}) => state.assetList.find((item) => isSameAsset(item, payload)) || null
  },
  actions: {
    reset() {
      Object.assign(this, defaultState());
    },
    setAssetList(list, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      const localTokens = profileStore.getChainTokens(resolvedChainNum);
      this.assetList = mergeLocalTokens(list, localTokens);
    },
    setCurrentAsset(asset) {
      this.currentAsset = asset ? normalizeAsset(asset) : null;
    },
    syncCurrentAsset(payload = {}) {
      const matched = this.findAssetByKey(payload);
      if (matched) {
        this.currentAsset = matched;
      }
      return this.currentAsset;
    },
    async fetchAssetList(address, config = {}) {
      const profileStore = useProfileStore();
      const resolvedChainNum = profileStore.currentNetwork?.chainNum;
      if (!address) {
        this.setAssetList([], resolvedChainNum);
        return [];
      }
      const result = await pcCardAssetList({ address }, config);
      const rows = Array.isArray(result?.list) ? result.list : [];
      this.setAssetList(rows, resolvedChainNum);
      return this.assetList;
    },
    async ensureCurrentAsset({ address, ...payload } = {}, config = {}) {
      const existing = this.findAssetByKey(payload);
      if (existing) {
        this.currentAsset = existing;
        return existing;
      }

      await this.fetchAssetList(address, config);
      const matched = this.findAssetByKey(payload);
      this.currentAsset = matched;
      return matched;
    },
    upsertLocalToken(token, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      profileStore.addChainToken(token, resolvedChainNum);
      this.setAssetList(this.assetList, resolvedChainNum);
      return this.findAssetByKey(token);
    },
    removeLocalToken(contractAddress, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      profileStore.removeChainToken(contractAddress, resolvedChainNum);
      this.setAssetList(
        this.assetList.filter((item) => String(item?.contractAddress || '').trim().toLowerCase() !== String(contractAddress || '').trim().toLowerCase()),
        resolvedChainNum
      );
      if (String(this.currentAsset?.contractAddress || '').trim().toLowerCase() === String(contractAddress || '').trim().toLowerCase()) {
        this.currentAsset = null;
      }
      return this.assetList;
    }
  }
});
